import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState, useMemo } from 'react'
import useAxiosPrivateSAI from '../../../hooks/useAxiosPrivateSAI'
import { faInfoCircle, faPlus, faCheck, faSpinner, faUserShield, faCircleXmark, faSchool, faFile } from '@fortawesome/free-solid-svg-icons'
import SideBar from '../../SideBar'
import moment from 'moment';
import useAuth from '../../../hooks/useAuth'
import {toast} from 'sonner'

const PolicyInfo = () => {
  const axios = useAxiosPrivateSAI()
  const { auth } = useAuth()
  const [policyData, setPolicyData] = useState(null)
  const [orgType, setOrgType] = useState(null)
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false)
  const [showBenefitsModal, setShowBenefitsModal] = useState(false)
  const openUpgrade = () => setIsUpgradeModalOpen(true)
  const closeUpgrade = () => setIsUpgradeModalOpen(false)
  const [items, setItems] = useState([])
  const toggleModal = () => {
    setShowBenefitsModal(!showBenefitsModal)
  }

  function safeParse(json) {
    try { return json ? JSON.parse(json) : null; } catch { return null; }
  }

  const policyInfo = useMemo(
    () => safeParse(policyData?.policy_information) ?? {},
    [policyData]
  );
  
  useEffect(() => {
    setPolicyData(JSON.parse(localStorage.getItem('policyData')));
    setOrgType(JSON.parse(localStorage.getItem('orgType')));
    
    if (policyData?.policy_id) {
      setItems(policyInfo?.items || []);
    }
  }, [policyData?.policy_id])

  const totals = useMemo(() => {
    return items.reduce(
      (accumulator, item) => {
        const num = (value) => (value == null || value === '' ? 0 : Number(value))
        accumulator.purchasedFte += num(item.fte);
        accumulator.submittedFte += num(item.submitted ?? item.submitted_sc);
        accumulator.maternityFte += num(item.maternity)
        return accumulator
      },
      { purchasedFte: 0, submittedFte: 0, maternityFte: 0 }
    )
  }, [items])

  const totalQuotedNumber = React.useMemo(() => {
    const items = policyInfo?.items ?? []
    return items.reduce((sum, item) => {
      const quote = item?.quoted != null ? parseFloat(item.quoted) : 0
      return sum + (Number.isFinite(quote) ? quote : 0)
    }, 0)
  }, [policyInfo?.items])

  const totalQuotedDisplay = React.useMemo(() => {
    let formatted = totalQuotedNumber.toFixed(2)
    if (totalQuotedNumber < 10) {
      formatted = formatted.replace(/^0+/, '')
    }
    return formatted
  }, [totalQuotedNumber])

  const formatDate = (date) => (date ? moment(date).format('DD/MM/YYYY') : '—')
  const fmt = number => Number(number).toFixed(2)

  return (
    <div className="w-full">
      {isUpgradeModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full shadow-lg relative">
            <button
              onClick={closeUpgrade}
              className="absolute top-2 right-2 text-gray-500 hover:text-red-600"
              aria-label="Close"
            >
              <FontAwesomeIcon icon={faCircleXmark} />
            </button>
            <h2 className="text-xl font-bold mb-4 text-sas">Contact Us For More Details</h2>
            {policyData?.department === 'Mutual' || policyData?.department === "GBS" ? (
              <a href='tel:01773814400' className="mb-2"><strong>Phone:</strong> 01773 814 400</a>
            ) : (
              <a href='tel:01773851079' className="mb-2"><strong>Phone:</strong> 01773 851 079</a>
            )}
            {policyData?.department === 'Mutual' || policyData?.department === "GBS" ? (
              <p className="mb-2"><strong>Email:</strong> <a href="mailto:enquiries@schoolsmutual.co.uk" className="text-sas underline">enquiries@schoolsmutual.co.uk</a></p>
            ) : (
              <p className="mb-2"><strong>Email:</strong> <a href="mailto:support@schoolsadvisoryservice.co.uk" className="text-sas underline">support@schoolsadvisoryservice.co.uk</a></p>
            )}
            <p className="text-sm text-gray-600 mt-4">Our team will be happy to assist with upgrades and optional extras.</p>
          </div>
        </div>
      )}
      <div className="mb-8">
        <h3 className="text-2xl sm:text-3xl md:text-5xl font-bold text-white text-center pt-6">
          Your Policy Information
        </h3>
        <h4 className="text-lg font-medium text-white text-center">
          Welcome to your Staff Absence Portal. Here you can view your policy details.
        </h4>
      </div>
      {/* Main Content Layout */}
      <div className="flex flex-col lg:flex-row max-w-[90rem] mx-auto lg:space-x-6 mt-24">
        {/* Sidebar Card */}
        <SideBar active={'Dashboard'} />
        {/* Main Card Section */}
        <main className="w-full lg:w-4/5 space-y-4 text-sas-dark">
          <div className='font-semibold text-xl'>Overview</div>
          <div className='flex items-center w-full space-x-4'>
            <div className='bg-white rounded-lg shadow-md w-full flex items-center'>
             <div className='bg-pink-100 py-4 px-6 rounded-l-lg'> <FontAwesomeIcon icon={faSchool} className='text-pink-500 h-7 w-7' /></div>
              <div className='pl-4'>
                <div className='text-sm'>School</div>
                <div>{orgType?.name}</div>
              </div>
            </div>
            <div className='bg-white rounded-lg shadow-md w-full flex items-center'>
              <div className='bg-pink-100 py-4 px-6 rounded-l-lg'> <FontAwesomeIcon icon={faFile} className='text-pink-500 h-7 w-7' /></div>
              <div className='pl-4'>
                <div className='text-sm'>Policy Type</div>
                <div>{policyData?.product}</div>
              </div>  
            </div>
          </div>
          <div className='w-full space-y-2'>
              <div className='font-semibold text-xl'>Policy Cover Summary</div>
             {/* General Information */}
              <div className='bg-white rounded-lg shadow-md p-4 w-full space-y-1 ' >
                <div className='text-base font-semibold mb-4'>General Information</div>
                <div className='grid grid-cols-3 gap-2'>
                  <div className="text-sm">Policy Reference: <strong>{policyData?.policy_reference || '—'}</strong></div>
                  <div className='text-sm'>Cover Start Date: <strong>{formatDate(policyData?.policy_inception)}</strong></div>
                  <div className='text-sm'>Cover End Date: <strong>{formatDate(policyData?.policy_expire)}</strong></div>
                  <div className='text-sm'>No. of Staff Quoted (FTE): <strong>{totalQuotedDisplay}</strong></div>
                  <div className="text-sm">Insurer: <strong>{policyData?.insurer || '—'}</strong></div>
                  <div className="text-sm">Payment Method: <strong>{policyData?.policy_payment_type || '—'}</strong></div>
                  <div className="text-sm">Annual Premium: <strong>£{Number(policyData?.policy_premium || 0).toLocaleString()}</strong></div>
                  <div className="text-sm">Auto-renew: <strong>{policyData?.auto_renew ? 'Yes' : 'No'}</strong></div>
                  <div className="text-sm">Account Manager: <strong className="text-sas">{policyData?.policy_owner_name || '—'}</strong></div>
                </div>
              </div>
          </div>
          {/* School Information (per staff type) */}
          <div className='w-full space-y-2'>
              <div className='text-xl font-semibold pt-4 pb-2'>School Information</div>
              <div className="overflow-x-auto rounded-lg bg-white shadow-md">
                <table className="min-w-full text-[12px] text-left text-gray-800 overflow-hidden">
                  <thead>
                    <tr className="bg-sas text-white text-sm">
                      <th className="p-4 font-semibold">Staff Type</th>
                      <th className="p-4 font-semibold">Purchased FTE</th>
                      <th className="p-4 font-semibold">Submitted FTE</th>
                      <th className="p-4 font-semibold">Benefit</th>
                      <th className="p-4 font-semibold">Excess</th>
                      <th className="p-4 font-semibold">Period</th>
                      <th className="p-4 font-semibold">Stress</th>
                      <th className="p-4 font-semibold">Maternity FTE</th>
                      <th className="p-4 font-semibold">Maternity Cover</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row, idx) => (
                      <tr key={idx} className="border-b text-center text-sm">
                        <td className="px-4 py-2 text-left">{row.staffType}</td>
                        <td className="px-4 py-2">{row.fte ?? 0}</td>
                        <td className="px-4 py-2">{row.submitted ?? row.submitted_sc ?? 0}</td>
                        <td className="px-4 py-2">{row.benefit ?? '—'}</td>
                        <td className="px-4 py-2">{row.excess ?? '—'}</td>
                        <td className="px-4 py-2">{row.period ?? '—'}</td>
                        <td className="px-4 py-2">{row.stress ?? '—'}</td>
                        <td className="px-4 py-2">{row.maternity ?? 0}</td>
                        <td className="px-4 py-2">{row.maternityCover ?? '—'}</td>
                      </tr>
                    ))}
                    {items.length > 0 && (
                      <tr className="font-semibold text-center bg-white text-sm">
                        <td className="px-4 py-2 text-left">Totals</td>
                        <td className="px-4 py-2">{fmt(totals.purchasedFte)}</td>
                        <td className="px-4 py-2">{fmt(totals.submittedFte)}</td>
                        <td className="px-4 py-2">—</td>
                        <td className="px-4 py-2">—</td>
                        <td className="px-4 py-2">—</td>
                        <td className="px-4 py-2">—</td>
                        <td className="px-4 py-2">{fmt(totals.maternityFte)}</td>
                        <td className="px-4 py-2">—</td>
                      </tr>
                    )}
                    {items.length === 0 && (
                      <tr><td className="p-4 text-center text-gray-500" colSpan={9}>No staff type information found.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
          </div>
          <div className='font-semibold text-xl pt-4'>What you can access as part of your policy</div>
          <div className='flex items-center w-full'>
            <div className='bg-white rounded-lg shadow-md p-4 w-full'>
            { policyData?.department === "Mutual" || policyData?.department === "GBS" ? (
                <div className="grid md:grid-cols-3 gap-6 text-sm">
                  <div>
                    <p className="font-semibold mb-2">Included in your {policyData?.product} { policyData?.department === "Mutual" || policyData?.department === "GBS" ? 'Scheme' : 'Policy' }:</p>
                    <ul className="space-y-1 text-gray-800">
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Jury service</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Sickness</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Suspension</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Bereavement leave</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Stress-related absences</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Compassionate leave</li>
                    </ul> 
                  </div>

                  <div>
                    <ul className="mt-[32px] space-y-1 text-gray-800">
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Stranded staff</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Accidents in or out of work</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Phased return to work</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Adoption leave</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Maternity leave</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Paternity leave</li>
                    </ul>
                  </div>

                  {/* Upgrade Info */}
                  {policyData?.product?.toLowerCase().includes("enhanced") ? (
                    <div>
                      <ul className="mt-[32px] space-y-1 text-gray-800">
                        <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Pre-existing conditions</li>
                        <li><FontAwesomeIcon icon={faCheck} className="text-[#8acb94] mr-2" /> Pre-planned operations</li>
                      </ul>
                    </div>
                  ) : (
                    <div>
                      <p className="font-semibold mb-2">Upgrade to Enhanced Protection Option for:</p>
                      <p className="text-sm"> <FontAwesomeIcon icon={faPlus} className="text-[#8acb94] mr-2" />Pre-existing conditions  </p>
                      <p className="text-sm"> <FontAwesomeIcon icon={faPlus} className="text-[#8acb94] mr-2" />Pre-planned surgery </p>
                      <p onClick={() => openUpgrade()} className="text-[#8acb94] underline cursor-pointer mt-4">Contact us for more details</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid md:grid-cols-4 gap-6 text-sm w-full">
                  <div className='col-span-2'>
                    <p className="font-semibold mb-2">Included in your SAS Policy:</p>
                    <div className='grid grid-cols-2 gap-2'>
                    <ul className="list-none space-y-2">
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Sickness or injury absences</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Accidents in or out of work</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Phased returns to work</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Bereavement</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Compassionate leave</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Jury Service</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Dedicated account manager</li>
                    </ul>
                    <ul>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Suspension</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Stranded staff abroad</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Ofsted Inspection engagement duties</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Trade union/association duties</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Online claims portal</li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Stress related absences <span className="text-xs text-gray-500">(where purchased)</span></li>
                      <li><FontAwesomeIcon icon={faCheck} className="text-[#009CDE] mr-2" />Maternity/Parental leave <span className="text-xs text-gray-500">(where purchased)</span></li>
                    </ul>
                  </div>
                  </div>

                  {/* Upgrade Info */}
                  <div>
                    <p className="font-semibold mb-2">Upgrade to SAS Policy Plus for:</p>
                    <ul className="list-none space-y-1">
                      <li> <FontAwesomeIcon icon={faPlus} className="text-sas mr-2" />Pre-existing conditions</li>
                      <li> <FontAwesomeIcon icon={faPlus} className="text-sas mr-2" />Pre-planned surgery</li>
                    </ul>
                    <div onClick={() => openUpgrade()} className="text-sas cursor-pointer underline text-xs mt-2 inline-block">Contact us for more details</div>
                  </div>

                  {/* Optional Extras */}
                  <div>
                    <p className="font-semibold mb-2">Optional Extras</p>
                    <ul className="list-none space-y-1 text-red-600">
                      <li><FontAwesomeIcon icon={faPlus} className=" mr-2" /> Travel Insurance</li>
                      <li><FontAwesomeIcon icon={faPlus} className=" mr-2" /> Cyber Insurance</li>
                    </ul>
                    <div onClick={() => openUpgrade()}  className="text-sas cursor-pointer underline text-xs mt-2 inline-block">Contact us for more details</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}

export default PolicyInfo
